import React from 'react';
import { getPortfolioEditElement, PORTFOLIO_TAGS } from './portfolioElementRegistry.jsx';

export const HTML_EDITOR_VERSION = 1;

export const cloneEditorValue = (value) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
};

export const VOID_ELEMENTS = new Set([
  'br',
  'col',
  'hr',
  'img',
  'source',
]);

export const SAFE_TAGS = PORTFOLIO_TAGS;

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const BOOLEAN_ATTRIBUTES = new Set([
  'allowfullscreen', 'async', 'autofocus', 'autoplay', 'checked', 'controls',
  'defer', 'disabled', 'hidden', 'loop', 'multiple', 'muted', 'open',
  'readonly', 'required', 'reversed', 'selected',
]);
const ALLOWED_IFRAME_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'www.google.com',
];

export const isTextNode = (node) => Boolean(node && node.type === 'text');
export const isElementNode = (node) => Boolean(node && typeof node.type === 'string' && node.type !== 'text');
export const isVoidElement = (type) => VOID_ELEMENTS.has(String(type || '').toLowerCase());

export const createStableId = (prefix = 'element') =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const createTextNode = (content = '') => ({
  id: createStableId('text'),
  type: 'text',
  content,
});

export const createElementNode = (type = 'div', attributes = {}, children = []) => {
  const safeType = sanitizeTagName(type);
  return {
    id: createStableId(safeType === 'img' ? 'image' : 'element'),
    type: safeType,
    attributes: normalizeAttributes(attributes, safeType),
    children: isVoidElement(safeType) ? [] : sanitizeChildren(children),
  };
};

export const createEmptyEditorDocument = () => ({
  version: HTML_EDITOR_VERSION,
  root: createElementNode('div'),
  classes: {},
});

export const sanitizeTagName = (type) => {
  const normalized = String(type || '').trim().toLowerCase();
  if (!/^[a-z][a-z0-9-]*$/.test(normalized)) {
    return 'div';
  }

  if (!SAFE_TAGS.has(normalized)) {
    return 'div';
  }

  return normalized;
};

export const isSafeUrl = (value, tagName = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return true;
  }

  if (/^(javascript|vbscript|data:text\/html)/i.test(trimmed)) {
    return false;
  }

  if (tagName === 'img' && /^data:image\/(jpeg|jpg|png|gif|webp);base64,/i.test(trimmed)) {
    return true;
  }

  if (/^(https?:|mailto:|tel:|#|\/|\.\.?\/)/i.test(trimmed)) {
    return true;
  }

  return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
};

const isSafeIframeUrl = (value) => {
  if (!value || !isSafeUrl(value, 'iframe')) {
    return false;
  }

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://example.com';
    const parsed = new URL(value, baseUrl);
    return ALLOWED_IFRAME_HOSTS.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch (_) {
    return false;
  }
};

export const normalizeClassList = (value) => {
  const rawClasses = Array.isArray(value)
    ? value
    : String(value || '').split(/\s+/);

  return [...new Set(rawClasses
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .filter((entry) => /^[A-Za-z_-][A-Za-z0-9_-]*$/.test(entry)))];
};

export const normalizeAttributes = (attributes = {}, tagName = 'div') => {
  const normalized = {};

  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) {
    return normalized;
  }

  Object.entries(attributes).forEach(([key, value]) => {
    const attrName = String(key || '').trim();
    const lowerName = attrName.toLowerCase();

    if (!attrName || UNSAFE_KEYS.has(attrName) || lowerName.startsWith('on')) {
      return;
    }

    if (lowerName === 'class' || lowerName === 'classname') {
      const classes = normalizeClassList(value);
      if (classes.length > 0) {
        normalized.class = classes;
      }
      return;
    }

    if (['href', 'src', 'action', 'poster'].includes(lowerName)) {
      if (tagName === 'iframe' && lowerName === 'src') {
        if (isSafeIframeUrl(value)) {
          normalized[attrName] = String(value);
        }
        return;
      }

      if (isSafeUrl(value, tagName)) {
        normalized[attrName] = String(value);
      }
      return;
    }

    if (BOOLEAN_ATTRIBUTES.has(lowerName)) {
      normalized[attrName] = Boolean(value);
      return;
    }

    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      normalized[attrName] = value;
      return;
    }

    normalized[attrName] = String(value);
  });

  return normalized;
};

const sanitizeChildren = (children = []) => (
  Array.isArray(children)
    ? children.map((child) => sanitizeNode(child)).filter(Boolean)
    : []
);

const arraysShareEntries = (left, right) => (
  Array.isArray(left)
  && Array.isArray(right)
  && left.length === right.length
  && left.every((entry, index) => normalizedValuesEqual(entry, right[index]))
);

const normalizedValuesEqual = (left, right) => {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) return arraysShareEntries(left, right);
  if (left && right && typeof left === 'object' && typeof right === 'object') {
    return recordsAreEquivalent(left, right);
  }
  return false;
};

const recordsAreEquivalent = (left, right) => {
  if (!left || typeof left !== 'object' || Array.isArray(left)) return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key)
      && normalizedValuesEqual(left[key], right[key]));
};

export const sanitizeNode = (node) => {
  if (!node || typeof node !== 'object' || [...UNSAFE_KEYS].some((key) => Object.prototype.hasOwnProperty.call(node, key))) {
    return null;
  }

  if (node.type === 'text') {
    const safeTextNode = {
      id: String(node.id || createStableId('text')),
      type: 'text',
      content: String(node.content || ''),
    };
    return node.id === safeTextNode.id
      && node.content === safeTextNode.content
      && Object.keys(node).length === 3
      ? node
      : safeTextNode;
  }

  const type = sanitizeTagName(node.type);
  const attributes = normalizeAttributes(node.attributes, type);

  const safeNode = {
    id: String(node.id || createStableId(type === 'img' ? 'image' : 'element')),
    type,
    attributes,
    children: isVoidElement(type) ? [] : sanitizeChildren(node.children),
  };

  return node.id === safeNode.id
    && node.type === safeNode.type
    && Object.keys(node).length === 4
    && recordsAreEquivalent(node.attributes, safeNode.attributes)
    && arraysShareEntries(node.children, safeNode.children)
    ? node
    : safeNode;
};

export const validateEditorDocument = (documentLike) => {
  const source = documentLike && typeof documentLike === 'object' ? documentLike : {};
  const root = sanitizeNode(source.root);

  const safeClasses = sanitizeClassDefinitions(source.classes);
  const version = Number(source.version) || HTML_EDITOR_VERSION;
  const document = {
    version,
    root: root && isElementNode(root) ? root : createEmptyEditorDocument().root,
    classes: safeClasses,
  };

  const classesAreEquivalent = recordsAreEquivalent(source.classes, safeClasses);
  const normalizedDocument = classesAreEquivalent
    ? { ...document, classes: source.classes }
    : document;
  const safeDocument = source.version === version
    && source.root === document.root
    && classesAreEquivalent
    && Object.keys(source).length === 3
    ? source
    : normalizedDocument;

  return { valid: true, document: safeDocument };
};

export const isEditorDocument = (value) => Boolean(value && typeof value === 'object' && value.root && value.version);

export const extractEditorDocument = (jsonData) => {
  if (isEditorDocument(jsonData?.content)) {
    return validateEditorDocument(jsonData.content).document;
  }

  if (isEditorDocument(jsonData)) {
    return validateEditorDocument(jsonData).document;
  }

  if (Array.isArray(jsonData?.content)) {
    return migrateOldDocumentIfRequired(jsonData).content;
  }

  return createEmptyEditorDocument();
};

export const withEditorDocument = (jsonData, editorDocument) => ({
  ...(jsonData || {}),
  content: validateEditorDocument(editorDocument).document,
});

export const migrateOldDocumentIfRequired = (jsonData) => {
  if (!jsonData || !Array.isArray(jsonData.content)) {
    return jsonData;
  }

  return {
    ...jsonData,
    content: {
      version: HTML_EDITOR_VERSION,
      root: createElementNode('div', {}, jsonData.content.map(migrateOldItem).filter(Boolean)),
      classes: {},
    },
    migratedFromBlockFormat: true,
  };
};

const textChildren = (value) => [createTextNode(String(value || ''))];

export const migrateOldItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const paddingClasses = normalizeClassList([item.paddingT, item.paddingB, item.paddingL, item.paddingR]);

  if (item.type === 'title') {
    return createElementNode('h1', { class: paddingClasses }, textChildren(item['text-content']));
  }
  if (item.type === 'subtitle') {
    return createElementNode('h2', { class: paddingClasses }, textChildren(item['text-content']));
  }
  if (item.type === 'paragraph') {
    return createElementNode('p', { class: paddingClasses }, textChildren(item['text-content']));
  }
  if (item.type === 'link') {
    return createElementNode('a', {
      href: item.href || '#',
      target: item.openInNewTab ? '_blank' : '',
      rel: item.openInNewTab ? 'noopener noreferrer' : '',
      class: ['btn', `btn-${item.variant || 'primary'}`, ...paddingClasses],
    }, textChildren(item.text || 'Visit Link'));
  }
  if (item.type === 'image') {
    return createElementNode('img', {
      src: item.src || '',
      alt: item.alt || 'Image',
      class: ['img-fluid', ...paddingClasses],
      'data-image-id': item.imageId || '',
    }, []);
  }
  if (item.type === 'ul' || item.type === 'ol') {
    return createElementNode(item.type, { class: paddingClasses }, (item.list || []).map((entry) =>
      createElementNode('li', {}, textChildren(entry))
    ));
  }
  if (item.type === 'section') {
    return createElementNode('section', { class: paddingClasses }, (item.content || []).map(migrateOldItem).filter(Boolean));
  }
  if (['col-2', 'col-3', 'col-4'].includes(item.type)) {
    const columns = Number(item.type.replace('col-', '')) || 2;
    return createElementNode('div', { class: ['row', ...paddingClasses] }, (item.columns || []).slice(0, columns).map((column) =>
      createElementNode('div', {
        class: columns === 2 ? ['col-12', 'col-md-6'] : columns === 3 ? ['col-12', 'col-md-4'] : ['col-12', 'col-md-6', 'col-lg-3'],
      }, (column.content || []).map(migrateOldItem).filter(Boolean))
    ));
  }

  return createElementNode('div', { class: paddingClasses }, textChildren(item['text-content'] || item.title || ''));
};

const reactAttributeCache = new WeakMap();

const attributesToReactProps = (attributes) => {
  const props = {};

  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'class') {
      if (Array.isArray(value) && value.length > 0) {
        props.className = value.join(' ');
      }
      return;
    }

    if (key === 'for') {
      props.htmlFor = value;
      return;
    }

    if (key === 'style' && typeof value === 'string') {
      return;
    }

    props[key] = value;
  });

  return props;
};

export const reactAttributesForValidatedNode = (node) => {
  if (!node || typeof node !== 'object') return {};
  const cached = reactAttributeCache.get(node);
  if (cached) return cached;

  const props = Object.freeze(attributesToReactProps(node.attributes || {}));
  reactAttributeCache.set(node, props);
  return props;
};

export const reactAttributesForNode = (node) => (
  attributesToReactProps(normalizeAttributes(node?.attributes, node?.type))
);

export const renderValidatedEditorNode = (node, options = {}) => {
  if (!node) return null;
  if (isTextNode(node)) return node.content;

  const EditElement = getPortfolioEditElement(node.type);
  const props = {
    ...reactAttributesForValidatedNode(node),
    key: node.id,
  };

  if (typeof options.onSelect === 'function') {
    props.onClick = (event) => {
      event.stopPropagation();
      options.onSelect(node.id);
    };
    props['data-editor-node-id'] = node.id;
    props.className = [props.className, options.selectedId === node.id ? 'html-editor-selected' : ''].filter(Boolean).join(' ');
  }

  if (isVoidElement(node.type)) return React.createElement(EditElement, props);
  return React.createElement(
    EditElement,
    props,
    (node.children || []).map((child) => renderValidatedEditorNode(child, options))
  );
};

export const renderEditorNode = (node, options = {}) => {
  const safeNode = sanitizeNode(node);
  return safeNode ? renderValidatedEditorNode(safeNode, options) : null;
};

const STYLE_PROPERTY_MAP = {
  backgroundColor: 'background-color',
  borderRadius: 'border-radius',
  boxShadow: 'box-shadow',
  fontFamily: 'font-family',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  letterSpacing: 'letter-spacing',
  lineHeight: 'line-height',
  maxHeight: 'max-height',
  maxWidth: 'max-width',
  minHeight: 'min-height',
  minWidth: 'min-width',
  textAlign: 'text-align',
};

const BREAKPOINTS = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
};

export const camelToKebab = (property) =>
  STYLE_PROPERTY_MAP[property] || String(property).replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

export const isValidClassName = (className) => /^[A-Za-z_-][A-Za-z0-9_-]*$/.test(String(className || '').trim());

export const sanitizeClassDefinitions = (classes = {}) => {
  if (!classes || typeof classes !== 'object' || Array.isArray(classes)) {
    return {};
  }

  return Object.entries(classes).reduce((acc, [className, definition]) => {
    const safeClassName = String(className || '').trim();
    if (!isValidClassName(safeClassName) || !definition || typeof definition !== 'object' || UNSAFE_KEYS.has(safeClassName)) {
      return acc;
    }

    acc[safeClassName] = ['base', 'sm', 'md', 'lg', 'xl'].reduce((styleAcc, breakpoint) => {
      styleAcc[breakpoint] = sanitizeStyleObject(definition[breakpoint]);
      return styleAcc;
    }, {});

    return acc;
  }, {});
};

export const sanitizeStyleObject = (styles = {}) => {
  if (!styles || typeof styles !== 'object' || Array.isArray(styles)) {
    return {};
  }

  return Object.entries(styles).reduce((acc, [property, value]) => {
    if (!property || UNSAFE_KEYS.has(property) || value === null || value === undefined) {
      return acc;
    }

    acc[property] = String(value);
    return acc;
  }, {});
};

const cssEscapeClass = (className) => String(className).replace(/[^A-Za-z0-9_-]/g, '\\$&');

const styleObjectToCss = (styles) => Object.entries(sanitizeStyleObject(styles))
  .map(([property, value]) => {
    const normalizedValue = String(value).trim().replace(/\s*!important\s*$/i, '');
    return `  ${camelToKebab(property)}: ${normalizedValue} !important;`;
  })
  .join('\n');

const customClassSelector = (className) => {
  const escapedClassName = cssEscapeClass(className);
  return [
    `.html-editor-canvas.${escapedClassName}`,
    `.html-editor-canvas .${escapedClassName}`,
  ].join(',\n');
};

export const generateCustomCss = (classes = {}) => {
  const safeClasses = sanitizeClassDefinitions(classes);
  const blocks = [];

  Object.entries(safeClasses).forEach(([className, definition]) => {
    const selector = customClassSelector(className);
    const baseCss = styleObjectToCss(definition.base);
    if (baseCss) {
      blocks.push(`${selector} {\n${baseCss}\n}`);
    }

    Object.entries(BREAKPOINTS).forEach(([breakpoint, minWidth]) => {
      const breakpointCss = styleObjectToCss(definition[breakpoint]);
      if (breakpointCss) {
        blocks.push(`@media (min-width: ${minWidth}) {\n${selector} {\n${breakpointCss}\n}\n}`);
      }
    });
  });

  return blocks.join('\n\n');
};

export const findNodeById = (root, nodeId) => {
  if (!root || !nodeId) {
    return null;
  }

  if (root.id === nodeId) {
    return root;
  }

  if (!Array.isArray(root.children)) {
    return null;
  }

  for (const child of root.children) {
    const match = findNodeById(child, nodeId);
    if (match) {
      return match;
    }
  }

  return null;
};

export const findParentById = (root, nodeId, parent = null) => {
  if (!root || !nodeId) {
    return null;
  }

  if (root.id === nodeId) {
    return parent;
  }

  if (!Array.isArray(root.children)) {
    return null;
  }

  for (const child of root.children) {
    const match = findParentById(child, nodeId, root);
    if (match) {
      return match;
    }
  }

  return null;
};

/**
 * Finds one element path with a single mutable traversal. Consumers that only
 * need IDs can build a Set once instead of repeatedly searching each subtree.
 */
export const getElementPath = (root, nodeId) => {
  if (!root || !nodeId || isTextNode(root)) return [];

  const path = [];
  const visit = (node) => {
    if (!node || isTextNode(node)) return false;
    path.push(node);
    if (node.id === nodeId) return true;

    for (const child of node.children || []) {
      if (visit(child)) return true;
    }

    path.pop();
    return false;
  };

  return visit(root) ? path : [];
};

export const getElementSelectionContext = (root, nodeId, options = {}) => {
  const isSelectable = typeof options.isSelectable === 'function'
    ? options.isSelectable
    : () => true;
  const emptyContext = {
    path: [],
    parentId: null,
    previousSiblingId: null,
    nextSiblingId: null,
    firstChildId: null,
  };

  if (!root || !nodeId || isTextNode(root)) {
    return emptyContext;
  }

  const path = getElementPath(root, nodeId);
  if (path.length < 2 || !isSelectable(path[path.length - 1])) {
    return emptyContext;
  }

  const selectedNode = path[path.length - 1];
  const selectablePath = [
    path[0],
    ...path.slice(1).filter((node) => isSelectable(node)),
  ];
  const parentNode = path[path.length - 2];
  const selectableParent = selectablePath[selectablePath.length - 2];
  const siblings = (parentNode.children || []).filter(
    (child) => !isTextNode(child) && isSelectable(child)
  );
  const siblingIndex = siblings.findIndex((child) => child.id === selectedNode.id);
  const selectableChildren = (selectedNode.children || []).filter(
    (child) => !isTextNode(child) && isSelectable(child)
  );

  return {
    path: selectablePath,
    parentId: selectablePath.length > 2 ? selectableParent.id : null,
    previousSiblingId: siblingIndex > 0 ? siblings[siblingIndex - 1].id : null,
    nextSiblingId: siblingIndex >= 0 && siblingIndex < siblings.length - 1
      ? siblings[siblingIndex + 1].id
      : null,
    firstChildId: selectableChildren[0]?.id || null,
  };
};

export const updateNodeById = (root, nodeId, updater) => {
  if (!root || !nodeId) {
    return root;
  }

  if (root.id === nodeId) {
    return sanitizeNode(typeof updater === 'function' ? updater(root) : updater);
  }

  if (!Array.isArray(root.children)) {
    return root;
  }

  let changed = false;
  const children = root.children.map((child) => {
    const nextChild = updateNodeById(child, nodeId, updater);
    if (nextChild !== child) changed = true;
    return nextChild;
  }).filter(Boolean);

  return changed ? { ...root, children } : root;
};

export const removeNodeById = (root, nodeId) => {
  if (!root || root.id === nodeId || !Array.isArray(root.children)) {
    return root;
  }

  let changed = false;
  const children = [];

  root.children.forEach((child) => {
    if (child.id === nodeId) {
      changed = true;
      return;
    }

    const nextChild = removeNodeById(child, nodeId);
    if (nextChild !== child) changed = true;
    children.push(nextChild);
  });

  return changed ? { ...root, children } : root;
};

const insertValidatedNode = (root, targetId, nextNode, position) => {
  if (!root) return root;

  if (position === 'child' && root.id === targetId && !isTextNode(root) && !isVoidElement(root.type)) {
    return {
      ...root,
      children: [...(root.children || []), nextNode],
    };
  }

  if (!Array.isArray(root.children)) {
    return root;
  }

  const targetIndex = root.children.findIndex((child) => child.id === targetId);
  if (targetIndex >= 0 && position !== 'child') {
    const children = [...root.children];
    children.splice(position === 'before' ? targetIndex : targetIndex + 1, 0, nextNode);
    return { ...root, children };
  }

  let changed = false;
  const children = root.children.map((child) => {
    const nextChild = insertValidatedNode(child, targetId, nextNode, position);
    if (nextChild !== child) changed = true;
    return nextChild;
  });

  return changed ? { ...root, children } : root;
};

export const insertNode = (root, targetId, node, position = 'child') => {
  const nextNode = sanitizeNode(node);
  if (!root || !targetId || !nextNode) return root;
  return insertValidatedNode(root, targetId, nextNode, position);
};

export const duplicateNodeWithNewIds = (node) => {
  const safeNode = sanitizeNode(node);
  if (!safeNode) {
    return null;
  }

  if (isTextNode(safeNode)) {
    return { ...safeNode, id: createStableId('text') };
  }

  return {
    ...safeNode,
    id: createStableId(safeNode.type === 'img' ? 'image' : 'element'),
    children: isVoidElement(safeNode.type) ? [] : (safeNode.children || []).map(duplicateNodeWithNewIds).filter(Boolean),
  };
};

export const isDescendant = (root, ancestorId, nodeId) => {
  const ancestor = findNodeById(root, ancestorId);
  return Boolean(ancestor && findNodeById(ancestor, nodeId) && ancestorId !== nodeId);
};

export const moveNode = (root, nodeId, targetId, position = 'child') => {
  if (!root || !nodeId || !targetId || nodeId === root.id || nodeId === targetId || isDescendant(root, nodeId, targetId)) {
    return root;
  }

  const node = findNodeById(root, nodeId);
  if (!node) {
    return root;
  }

  const withoutNode = removeNodeById(root, nodeId);
  return insertNode(withoutNode, targetId, node, position);
};

export const reorderChild = (root, parentId, fromIndex, toIndex) => updateNodeById(root, parentId, (node) => {
  if (!Array.isArray(node.children)) {
    return node;
  }

  const boundedTo = Math.max(0, Math.min(toIndex, node.children.length - 1));
  if (fromIndex < 0 || fromIndex >= node.children.length || fromIndex === boundedTo) {
    return node;
  }

  const children = [...node.children];
  const [moved] = children.splice(fromIndex, 1);
  children.splice(boundedTo, 0, moved);
  return { ...node, children };
});

export const getClassUsageCounts = (root, classNames = []) => {
  const trackedClasses = normalizeClassList(classNames);
  const usageCounts = Object.fromEntries(trackedClasses.map((className) => [className, 0]));
  const trackedClassSet = new Set(trackedClasses);

  const visit = (node) => {
    if (!node || isTextNode(node)) return;

    normalizeClassList(node.attributes?.class).forEach((className) => {
      if (trackedClassSet.has(className)) usageCounts[className] += 1;
    });
    (node.children || []).forEach(visit);
  };

  visit(root);
  return usageCounts;
};

export const renameCustomClass = (document, oldName, newName) => {
  if (!isValidClassName(oldName) || !isValidClassName(newName) || oldName === newName) {
    return document;
  }

  const nextDocument = cloneEditorValue(document);
  const definition = nextDocument.classes?.[oldName];
  if (!definition) {
    return document;
  }

  nextDocument.classes[newName] = definition;
  delete nextDocument.classes[oldName];

  const replaceClass = (node) => {
    if (!node || isTextNode(node)) {
      return node;
    }

    const classes = normalizeClassList(node.attributes?.class).map((className) => className === oldName ? newName : className);
    return {
      ...node,
      attributes: {
        ...(node.attributes || {}),
        class: normalizeClassList(classes),
      },
      children: (node.children || []).map(replaceClass),
    };
  };

  nextDocument.root = replaceClass(nextDocument.root);
  return validateEditorDocument(nextDocument).document;
};
